/**
 * How a notice is filed and coloured — one definition, for every screen that shows notices.
 *
 * ## Why this file exists
 *
 * This logic lived in two places: the notifications page and the chat drawer's notifications
 * tab. Nothing kept them in step, and they drifted — the chat tab did not know about
 * ADVENTURE_OPENED, SESSION_INVITE, ADVENTURE_CHECKED_IN, CONTRIBUTION_DECIDED, the three
 * group-activity types, or that a badge belongs under Rewards. Every one of those filed as
 * "system" there and as something sensible on the page, and nothing failed loudly enough for
 * anyone to notice.
 *
 * Adding a notice type meant remembering both copies. Now it means editing this file, which is
 * the same shape the mobile client already settled on in `features/notifications/presentation.ts`.
 *
 * ## Adding a type
 *
 * A GUID also has to be copied into `api/types/notices.ts`, and — if it carries buttons or a
 * destination — into `api/services/noticeActions.ts` and `components/layout/MainLayout.tsx`.
 * None of those fail loudly when missed; the notice just renders generic and links nowhere.
 */
import type { Theme } from '@mui/material'
import { NoticeDataTypes, NoticeTypeIds, type UserNotice } from '@api/types/notices'

export type NotificationCategory = 'all' | 'social' | 'adventures' | 'rewards' | 'system'

export const CATEGORY_FILTERS: { key: NotificationCategory; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'social', label: 'Social' },
  { key: 'adventures', label: 'Adventures' },
  { key: 'rewards', label: 'Rewards' },
  { key: 'system', label: 'System' },
]

const upper = (id: string) => id.toUpperCase()

/** Notice types about people. */
const SOCIAL_TYPES = new Set(
  [
    NoticeTypeIds.FRIEND_REQUEST,
    NoticeTypeIds.FRIEND_REQUEST_ACCEPTED,
    NoticeTypeIds.GROUP_INVITATION,
    // A reply is somebody talking to you, whatever the discussion happens to hang off.
    NoticeTypeIds.DISCUSSION_REPLY,
    // Group activity is somebody in your group doing something — people, not places.
    NoticeTypeIds.GROUP_DISCUSSION_STARTED,
    NoticeTypeIds.GROUP_EVENT_POSTED,
    NoticeTypeIds.GROUP_TODO_ACTIVITY,
  ].map(upper),
)

/** Notice types about places, and about the groups going to them. */
const ADVENTURE_TYPES = new Set(
  [
    NoticeTypeIds.ADVENTURE_INVITATION,
    NoticeTypeIds.ADVENTURE_OPENED,
    NoticeTypeIds.MODERATOR_APPROVAL_REQUEST,
    NoticeTypeIds.SESSION_INVITE,
    // A visit recorded for you by arriving somewhere.
    NoticeTypeIds.ADVENTURE_CHECKED_IN,
    // An answer to a correction you suggested about an adventure.
    NoticeTypeIds.CONTRIBUTION_DECIDED,
    // A suggestion about an adventure you manage, waiting for you to accept or reject.
    NoticeTypeIds.CONTRIBUTION_RECEIVED,
    // Trail route review: a route waiting for a moderator, and the answer to one you sent.
    NoticeTypeIds.TRAIL_ROUTE_SUBMITTED,
    NoticeTypeIds.TRAIL_ROUTE_DECIDED,
    // Forming a group for an adventure, and settling when it goes. These file under adventures
    // rather than social: the subject is the outing, and the people are incidental to it.
    NoticeTypeIds.SESSION_MATCH_AVAILABLE,
    NoticeTypeIds.SESSION_SCHEDULE_DECIDED,
    NoticeTypeIds.SESSION_SPLIT_PROPOSED,
    // Your own group coming up, moved, called off.
    NoticeTypeIds.GROUP_SESSION_UPDATE,
    // Doing a Story together: asked, changed, a stop started, the drive started.
    NoticeTypeIds.STORY_PARTY_INVITE,
    NoticeTypeIds.STORY_PARTY_UPDATE,
    NoticeTypeIds.STORY_PARTY_STOP_STARTED,
    NoticeTypeIds.STORY_PARTY_DRIVE_STARTED,
  ].map(upper),
)

const SYSTEM_TYPES = new Set([NoticeTypeIds.SYSTEM, NoticeTypeIds.GENERAL].map(upper))

export function isGroupJoinRequest(notice: UserNotice): boolean {
  return notice.parsedData?.type === NoticeDataTypes.GROUP_JOIN_REQUEST
}

/** Which filter tab a notice belongs under. */
export function getNotificationCategory(notice: UserNotice): NotificationCategory {
  const type = upper(notice.type)

  if (SOCIAL_TYPES.has(type) || isGroupJoinRequest(notice)) return 'social'
  if (ADVENTURE_TYPES.has(type)) return 'adventures'

  // The Rewards filter existed for a long time with nothing ever returning it, because the one
  // notice that belongs there was wearing the trip invitation's id and filing under System.
  if (type === upper(NoticeTypeIds.BADGE_EARNED)) return 'rewards'

  if (SYSTEM_TYPES.has(type)) return 'system'

  // Anything unrecognised. A type added to the server but not here lands in System rather than
  // disappearing — visible and slightly wrong beats invisible and correct-looking.
  return 'system'
}

/** The colour of a notice's icon circle. */
export function getCategoryColor(notice: UserNotice, theme: Theme): string {
  const type = upper(notice.type)

  if (type === upper(NoticeTypeIds.FRIEND_REQUEST)) return theme.palette.primary.main
  if (type === upper(NoticeTypeIds.FRIEND_REQUEST_ACCEPTED)) return theme.palette.success.main
  if (type === upper(NoticeTypeIds.GROUP_INVITATION)) return theme.palette.info.main
  if (isGroupJoinRequest(notice)) return theme.palette.info.main
  if (type === upper(NoticeTypeIds.ADVENTURE_INVITATION)) return theme.palette.warning.main
  if (type === upper(NoticeTypeIds.DISCUSSION_REPLY)) return theme.palette.info.main
  if (type === upper(NoticeTypeIds.BADGE_EARNED)) return theme.palette.success.main
  // Also earned rather than asked of you — you were there.
  if (type === upper(NoticeTypeIds.ADVENTURE_CHECKED_IN)) return theme.palette.success.main
  if (type === upper(NoticeTypeIds.MODERATOR_APPROVAL_REQUEST)) return theme.palette.secondary.main
  // An invitation to something forming, and the answer when it settles.
  if (type === upper(NoticeTypeIds.SESSION_MATCH_AVAILABLE)) return theme.palette.primary.main
  if (type === upper(NoticeTypeIds.SESSION_SPLIT_PROPOSED)) return theme.palette.warning.main
  if (type === upper(NoticeTypeIds.SESSION_SCHEDULE_DECIDED)) return theme.palette.success.main
  if (type === upper(NoticeTypeIds.GROUP_SESSION_UPDATE)) return theme.palette.success.main
  if (type === upper(NoticeTypeIds.STORY_PARTY_INVITE)) return theme.palette.primary.main
  if (
    type === upper(NoticeTypeIds.STORY_PARTY_UPDATE) ||
    type === upper(NoticeTypeIds.STORY_PARTY_STOP_STARTED) ||
    type === upper(NoticeTypeIds.STORY_PARTY_DRIVE_STARTED)
  ) return theme.palette.success.main
  if (type === upper(NoticeTypeIds.SYSTEM)) return theme.palette.error.main

  return theme.palette.info.main
}

/**
 * Where a group notice (`GROUP_SESSION_UPDATE`) leads: the adventure with its group's lobby open
 * (`fromInvite` makes AdventurePlayer show the lobby rather than start), or just the adventure when
 * the group was called off or you were taken out of it. Null when the notice names no adventure.
 */
export function groupSessionNoticeTarget(
  notice: Pick<UserNotice, 'type' | 'parsedData'>,
): { path: string; state?: { sessionId: string; fromInvite: true } } | null {
  const story = storyPartyNoticeTarget(notice)
  if (story) return story
  if (upper(notice.type) !== upper(NoticeTypeIds.GROUP_SESSION_UPDATE)) return null
  const data = notice.parsedData as { sessionId?: unknown; adventureId?: unknown; kind?: unknown } | undefined
  if (typeof data?.adventureId !== 'string' || !data.adventureId) return null
  const path = `/adventures/${data.adventureId}`
  if (data.kind === 'cancelled' || data.kind === 'removed' || data.kind === 'ended' || typeof data.sessionId !== 'string' || !data.sessionId) {
    return { path }
  }
  return { path, state: { sessionId: data.sessionId, fromInvite: true } }
}

/**
 * Where a Story party notice leads: the stop's adventure (with its group session) once a stop has started, else the
 * Story's page. Null for any other notice, or one that names no Story. Also answered by
 * {@link groupSessionNoticeTarget}, so every notice list routes them without a branch of its own.
 */
export function storyPartyNoticeTarget(
  notice: Pick<UserNotice, 'type' | 'parsedData'>,
): { path: string; state?: { sessionId: string; fromInvite: true } } | null {
  const type = upper(notice.type)
  const isParty = [
    NoticeTypeIds.STORY_PARTY_INVITE,
    NoticeTypeIds.STORY_PARTY_UPDATE,
    NoticeTypeIds.STORY_PARTY_STOP_STARTED,
    NoticeTypeIds.STORY_PARTY_DRIVE_STARTED,
  ].some((id) => upper(id) === type)
  if (!isParty) return null
  const data = notice.parsedData as { storyId?: unknown; sessionId?: unknown; adventureId?: unknown } | undefined
  if (type === upper(NoticeTypeIds.STORY_PARTY_STOP_STARTED)
    && typeof data?.adventureId === 'string' && data.adventureId
    && typeof data.sessionId === 'string' && data.sessionId) {
    return { path: `/adventures/${data.adventureId}`, state: { sessionId: data.sessionId, fromInvite: true } }
  }
  return typeof data?.storyId === 'string' && data.storyId ? { path: `/stories/${data.storyId}` } : null
}
